import { Box, FormControl, Link, Option, Select } from "@mui/joy";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { capitalizeText } from "../../../lib/utils";
import {
  IFondo,
  IMateriale,
  IQualita,
  IStatoLavorazione,
} from "../../../models";
import { EOptionType, IFilterOption } from "../../../models/utlis";

interface ISelectMaterialeFiltersProps {
  materiali: IQualita[];
  tipologie: IMateriale[];
  colori: IFondo[];
  lavorazioni: IStatoLavorazione[];
  spessoriDisponibili: number[];
  filters: IFilterOption[];
  onSetFilters: (filters: IFilterOption[]) => void;
}

export default function SelectMaterialeFilters({
  materiali,
  tipologie,
  colori,
  lavorazioni,
  spessoriDisponibili,
  filters,
  onSetFilters,
}: ISelectMaterialeFiltersProps) {
  const { i18n, t } = useTranslation();
  const [selectedMateriale, setSelectedMateriale] = useState<string | null>(
    null
  );
  const [selectedTipologia, setSelectedTipologia] = useState<string | null>(
    null
  );
  const [selectedColore, setSelectedColore] = useState<string | null>(null);
  const [selectedLavorazione, setSelectedLavorazione] = useState<string | null>(
    null
  );
  const [selectedSpessore, setSelectedSpessore] = useState<string | null>(null);

  const materialiList = useMemo(
    () =>
      materiali.map((materiale) => ({
        id: materiale.id,
        label:
          i18n.language === "en" && materiale.nomeEn
            ? materiale.nomeEn
            : materiale.nome,
      })),
    [materiali, i18n]
  );

  const onChangeMateriale = (
    _event:
      | React.MouseEvent<Element, MouseEvent>
      | React.KeyboardEvent<Element>
      | React.FocusEvent<Element, Element>
      | null,
    value: string | null
  ) => {
    const _filters = [...filters].filter(
      (opt) => !(opt.type === EOptionType.qualita)
    );
    if (value) {
      onSetFilters([
        {
          type: EOptionType.qualita,
          numericValue: parseInt(value),
        },
      ]);
    } else {
      onSetFilters(_filters);
    }
  };

  const onChangeTipologia = (
    _event:
      | React.MouseEvent<Element, MouseEvent>
      | React.KeyboardEvent<Element>
      | React.FocusEvent<Element, Element>
      | null,
    value: string | null
  ) => {
    const _filters = [...filters].filter(
      (opt) => !(opt.type === EOptionType.materiale)
    );
    if (value) {
      onSetFilters([
        {
          type: EOptionType.materiale,
          numericValue: parseInt(value),
        },
      ]);
    } else {
      onSetFilters(_filters);
    }
  };

  const onChangeColore = (
    _event:
      | React.MouseEvent<Element, MouseEvent>
      | React.KeyboardEvent<Element>
      | React.FocusEvent<Element, Element>
      | null,
    value: string | null
  ) => {
    const _filters = [...filters].filter(
      (opt) => !(opt.type === EOptionType.fondo)
    );
    if (value) {
      onSetFilters([
        {
          type: EOptionType.fondo,
          numericValue: parseInt(value),
        },
      ]);
    } else {
      onSetFilters(_filters);
    }
  };
  const onChangeLavorazione = (
    _event:
      | React.MouseEvent<Element, MouseEvent>
      | React.KeyboardEvent<Element>
      | React.FocusEvent<Element, Element>
      | null,
    value: string | null
  ) => {
    const _filters = [...filters].filter(
      (opt) => !(opt.type === EOptionType.statoLavorazione)
    );
    if (value) {
      onSetFilters([
        {
          type: EOptionType.statoLavorazione,
          numericValue: parseInt(value),
        },
      ]);
    } else {
      onSetFilters(_filters);
    }
  };
  const onSelectedSpessore = (
    _event:
      | React.MouseEvent<Element, MouseEvent>
      | React.KeyboardEvent<Element>
      | React.FocusEvent<Element, Element>
      | null,
    value: string | null
  ) => {
    const _filters = [...filters].filter(
      (opt) => !(opt.type === EOptionType.misZ)
    );
    if (value) {
      onSetFilters([
        {
          type: EOptionType.misZ,
          numericValues: [parseFloat(value), parseFloat(value)],
        },
      ]);
    } else {
      onSetFilters(_filters);
    }
  };
  const onResetFilters = () => {
    const _filters = [...filters].filter(
      (opt) =>
        ![
          EOptionType.fullText,
          EOptionType.materiale,
          EOptionType.qualita,
          EOptionType.fondo,
          EOptionType.statoLavorazione,
          EOptionType.misZ,
        ].includes(opt.type)
    );
    onSetFilters(_filters);
  };

  useEffect(() => {
    const tipologia =
      filters.find((filter) => filter.type === EOptionType.materiale)
        ?.numericValue ?? null;
    const materiale =
      filters.find((filter) => filter.type === EOptionType.qualita)
        ?.numericValue ?? null;
    const colore =
      filters.find((filter) => filter.type === EOptionType.fondo)
        ?.numericValue ?? null;
    const finitura =
      filters.find((filter) => filter.type === EOptionType.statoLavorazione)
        ?.numericValue ?? null;
    const misZArray =
      filters.find((filter) => filter.type === EOptionType.misZ)
        ?.numericValues ?? null;
    setSelectedMateriale(materiale?.toString() ?? null);
    setSelectedTipologia(tipologia?.toString() ?? null);
    setSelectedColore(colore?.toString() ?? null);
    setSelectedLavorazione(finitura?.toString() ?? null);
    setSelectedSpessore(misZArray?.[0]?.toString() ?? null);
  }, [filters, materialiList]);
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <FormControl sx={{ py: 1 }}>
        <Select
          placeholder={capitalizeText(t("MaterialType")) + "..."}
          variant="plain"
          value={selectedTipologia}
          onChange={onChangeTipologia}
        >
          <Option value="">---</Option>
          {tipologie.map((tipologia) => {
            const nome =
              i18n.language === "en" && tipologia.nomeEn
                ? tipologia.nomeEn
                : tipologia.nome;
            return (
              <Option key={tipologia.id} value={tipologia.id.toString()}>
                {nome}
              </Option>
            );
          })}
        </Select>
      </FormControl>
      <FormControl sx={{ py: 1 }}>
        <Select
          placeholder={capitalizeText(t("Material")) + "..."}
          variant="plain"
          value={selectedMateriale}
          onChange={onChangeMateriale}
        >
          <Option value="">---</Option>
          {materiali.map((materiale) => {
            const nome =
              i18n.language === "en" && materiale.nomeEn
                ? materiale.nomeEn
                : materiale.nome;
            return (
              <Option key={materiale.id} value={materiale.id.toString()}>
                {nome}
              </Option>
            );
          })}
        </Select>
      </FormControl>
      <FormControl sx={{ py: 1 }}>
        <Select
          placeholder={capitalizeText(t("Color")) + "..."}
          variant="plain"
          value={selectedColore}
          onChange={onChangeColore}
        >
          <Option value="">---</Option>
          {colori.map((colore) => {
            const nome =
              i18n.language === "en" && colore.nomeEn
                ? colore.nomeEn
                : colore.nome;
            return (
              <Option key={colore.id} value={colore.id.toString()}>
                {nome}
              </Option>
            );
          })}
        </Select>
      </FormControl>
      <FormControl sx={{ py: 1 }}>
        <Select
          placeholder={capitalizeText(t("Thickness")) + "..."}
          variant="plain"
          value={selectedSpessore}
          onChange={onSelectedSpessore}
        >
          <Option value="">---</Option>
          {spessoriDisponibili.map((misZ) => (
            <Option key={misZ} value={misZ.toString()}>
              {misZ}
            </Option>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ py: 1 }}>
        <Select
          placeholder={capitalizeText(t("Finish")) + "..."}
          variant="plain"
          value={selectedLavorazione}
          onChange={onChangeLavorazione}
        >
          <Option value="">---</Option>
          {lavorazioni.map((lavorazione) => {
            const nome =
              i18n.language === "en" && lavorazione.nomeEn
                ? lavorazione.nomeEn
                : lavorazione.nome;
            return (
              <Option key={lavorazione.id} value={lavorazione.id.toString()}>
                {nome}
              </Option>
            );
          })}
        </Select>
      </FormControl>
      <Box sx={{ py: 2, display: "flex", justifyContent: "space-between" }}>
        <Link
          sx={{ ":hover": { background: "none" } }}
          onClick={onResetFilters}
          variant="plain"
        >
          {t("ResetFilters").toUpperCase()}
        </Link>
      </Box>
    </Box>
  );
}
